import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, LinearProgress, MenuItem, Stack, Table,
    TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import { Add, Edit, Restaurant } from '@mui/icons-material';
import axios from 'axios';
import Navbar from '../setup/Navbar';
import { fetchCategoryKitchens, assignCategoryKitchen, reset as resetCk } from '../../features/setup/categoryKitchenSlice';
import { fetchKitchens } from '../../features/setup/kitchenSlice';
import '../../services/GlobalVariables';

export default function CategoryKitchenMapping() {
    const dispatch = useDispatch();
    const { mappings, isLoading, isError, isSuccess, message } = useSelector(state => state.categoryKitchen);
    const { kitchens } = useSelector(state => state.kitchens);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({ categoryCode: '', categoryName: '', kitchenId: '' });
    const [itemRoutes, setItemRoutes] = useState([]);
    const [itemSearch, setItemSearch] = useState('');
    const [itemDialogOpen, setItemDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedKitchenIds, setSelectedKitchenIds] = useState([]);
    const [routeLoading, setRouteLoading] = useState(false);
    const [routeError, setRouteError] = useState('');

    const loadItemRoutes = async () => {
        setRouteLoading(true); setRouteError('');
        try {
            const response = await axios.get(`${global.SET_API_URL}categoryKitchen/itemRoutes`);
            setItemRoutes(response.data?.items || []);
        } catch (error) {
            setRouteError(error.response?.data?.message || error.message || 'Unable to load item routes');
        } finally { setRouteLoading(false); }
    };

    useEffect(() => {
        dispatch(fetchCategoryKitchens());
        dispatch(fetchKitchens());
        loadItemRoutes();
    }, [dispatch]);

    useEffect(() => {
        if (isSuccess && message) {
            dispatch(resetCk()); setDialogOpen(false); dispatch(fetchCategoryKitchens()); loadItemRoutes();
        }
        if (isError && message) dispatch(resetCk());
    }, [isSuccess, isError, message, dispatch]);

    const unmapped = useMemo(() => mappings.filter(m => !m.kitchenId), [mappings]);
    const filteredItems = useMemo(() => {
        const q = itemSearch.trim().toLowerCase();
        return itemRoutes.filter(item => !q || [item.itemCode, item.itemName, item.categoryName, item.primaryKitchenName, item.additionalKitchenNames]
            .some(value => String(value || '').toLowerCase().includes(q)));
    }, [itemRoutes, itemSearch]);

    const openCategoryDialog = (row) => {
        setFormData(row ? { categoryCode: row.categoryCode, categoryName: row.categoryName, kitchenId: row.kitchenId || '' }
            : { categoryCode: '', categoryName: '', kitchenId: '' });
        setDialogOpen(true);
    };
    const saveCategory = () => {
        if (!formData.categoryCode || !formData.kitchenId) return alert('Category and Kitchen are required');
        dispatch(assignCategoryKitchen({ categoryCode: formData.categoryCode, kitchenId: formData.kitchenId, isActive: 1 }));
    };
    const openItemDialog = (item) => {
        setSelectedItem(item);
        setSelectedKitchenIds(item.additionalKitchenIds || []);
        setItemDialogOpen(true);
    };
    const saveItemRoutes = async () => {
        if (!selectedItem) return;
        setRouteLoading(true); setRouteError('');
        try {
            await axios.post(`${global.SET_API_URL}categoryKitchen/itemRoutes`, {
                itemCode: selectedItem.itemCode, kitchenIds: selectedKitchenIds
            });
            setItemDialogOpen(false); await loadItemRoutes();
        } catch (error) {
            setRouteError(error.response?.data?.message || error.message || 'Unable to save item routes');
        } finally { setRouteLoading(false); }
    };

    return <>
        <Navbar />
        <Box sx={{ p: 2 }}>
            {(isLoading || routeLoading) && <LinearProgress sx={{ mb: 1 }} />}
            {routeError && <Alert severity="error" sx={{ mb: 2 }}>{routeError}</Alert>}
            <Card sx={{ mb: 2 }}><CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Box><Typography variant="h5" fontWeight="bold">Category to Primary Kitchen</Typography>
                        <Typography variant="body2" color="text.secondary">Every category keeps one primary kitchen.</Typography></Box>
                    <Button startIcon={<Add />} variant="contained" onClick={() => openCategoryDialog(unmapped[0] || null)}>Map Category</Button>
                </Stack>
                <Table size="small"><TableHead><TableRow>
                    <TableCell><strong>Category</strong></TableCell><TableCell><strong>Primary Kitchen</strong></TableCell>
                    <TableCell><strong>Printer</strong></TableCell><TableCell><strong>Status</strong></TableCell><TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow></TableHead><TableBody>
                    {mappings.map(m => <TableRow key={m.categoryCode}>
                        <TableCell>{m.categoryName}</TableCell><TableCell>{m.kitchenName || '-'}</TableCell>
                        <TableCell>{m.printerIP ? `${m.printerName || ''} ${m.printerIP}` : '-'}</TableCell>
                        <TableCell><Chip label={m.kitchenStatus || 'n/a'} size="small" color={m.kitchenStatus === 'active' ? 'success' : 'default'} /></TableCell>
                        <TableCell align="center"><IconButton size="small" color="primary" onClick={() => openCategoryDialog(m)}><Edit /></IconButton></TableCell>
                    </TableRow>)}
                </TableBody></Table>
            </CardContent></Card>

            <Card><CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Box><Typography variant="h5" fontWeight="bold">Item Additional Kitchens</Typography>
                        <Typography variant="body2" color="text.secondary">Selected items print in their primary kitchen and every additional kitchen below.</Typography></Box>
                    <TextField size="small" label="Search item" placeholder="Crown Crust, item code, category" value={itemSearch}
                        onChange={event => setItemSearch(event.target.value)} sx={{ minWidth: { md: 360 } }} />
                </Stack>
                <Table size="small"><TableHead><TableRow>
                    <TableCell><strong>Item</strong></TableCell><TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>Primary Kitchen</strong></TableCell><TableCell><strong>Additional Kitchens</strong></TableCell><TableCell align="center"><strong>Action</strong></TableCell>
                </TableRow></TableHead><TableBody>
                    {filteredItems.map(item => <TableRow key={item.itemCode} hover>
                        <TableCell><Typography fontWeight={700}>{item.itemName}</Typography><Typography variant="caption">Code: {item.itemCode}</Typography></TableCell>
                        <TableCell>{item.categoryName || '-'}</TableCell>
                        <TableCell><Chip size="small" color={item.primaryKitchenId ? 'primary' : 'warning'} label={item.primaryKitchenName || 'Not mapped'} /></TableCell>
                        <TableCell>{item.additionalKitchenIds?.length
                            ? <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">{String(item.additionalKitchenNames || '').split(', ').map(name => <Chip key={name} size="small" icon={<Restaurant />} label={name} color="success" variant="outlined" />)}</Stack>
                            : <Typography variant="body2" color="text.secondary">None</Typography>}</TableCell>
                        <TableCell align="center"><Button size="small" variant="outlined" onClick={() => openItemDialog(item)}>Configure</Button></TableCell>
                    </TableRow>)}
                    {!filteredItems.length && <TableRow><TableCell colSpan={5} align="center">No items found.</TableCell></TableRow>}
                </TableBody></Table>
            </CardContent></Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Map Category to Primary Kitchen</DialogTitle><DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label="Category" value={formData.categoryName} slotProps={{ input: { readOnly: true } }} />
                    <TextField select label="Kitchen" value={formData.kitchenId} onChange={e => setFormData({ ...formData, kitchenId: e.target.value })} required>
                        {kitchens.map(k => <MenuItem key={k.kitchenId} value={k.kitchenId}>{k.kitchenName} ({k.printerIP || 'no IP'})</MenuItem>)}
                    </TextField>
                </Stack></DialogContent><DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={saveCategory} variant="contained">Save</Button></DialogActions>
            </Dialog>

            <Dialog open={itemDialogOpen} onClose={() => setItemDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Additional Kitchens — {selectedItem?.itemName || ''}</DialogTitle><DialogContent>
                    <Alert severity="info" sx={{ my: 1.5 }}>Primary kitchen: <strong>{selectedItem?.primaryKitchenName || 'Not mapped'}</strong>. Select only extra destinations.</Alert>
                    <Autocomplete multiple options={kitchens.filter(k => Number(k.kitchenId) !== Number(selectedItem?.primaryKitchenId))}
                        value={kitchens.filter(k => selectedKitchenIds.includes(Number(k.kitchenId)))}
                        getOptionLabel={option => `${option.kitchenName} (${option.printerIP || 'no IP'})`}
                        isOptionEqualToValue={(option, value) => Number(option.kitchenId) === Number(value.kitchenId)}
                        onChange={(event, values) => setSelectedKitchenIds(values.map(value => Number(value.kitchenId)))}
                        renderInput={params => <TextField {...params} label="Additional Kitchens" placeholder="Select one or more kitchens" />} />
                </DialogContent><DialogActions><Button onClick={() => setItemDialogOpen(false)}>Cancel</Button><Button onClick={saveItemRoutes} variant="contained" disabled={routeLoading}>Save Routes</Button></DialogActions>
            </Dialog>
        </Box>
    </>;
}